import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { LicensePurchaseEventAdapter } from '~analytics/index';
import { PaywallScreens } from '~analytics/types';
import { translate } from '~l10n/client';
import Backbone from 'backbone';
import user from 'user';

import { toast } from '~shared/components/Toast';
import { legacySetPro, proCheck } from '~teacher/util/proCheck';
import AccountView from 'AccountView';
import activateLicenseModal from 'ActivateLicenseController';
import Constants from 'Constants';
import dots from 'Dots';
import goPro from 'GoProController';
import header from 'HeaderController';
import renewalNotice from 'RenewalController';
import teacher from 'TeacherModel';

import popup from '../../../../shared/components/popup/PopupController';
import { ErrorView } from '../../../shared/error/ErrorView';

let router = null;
const model = teacher.account;
const licenseModel = model.licenseModel;

class AccountController {
  doAccount(teacherRouter, wantsPro = false, applyLicense = false) {
    router = teacherRouter;
    header.render();

    if (router.isEnteringProfile()) {
      model.resetPasswordFields();
      model.restoreInitialValues();
      model.banner.message = '';
    }

    model.buyingLicense = wantsPro;
    if (!model.initialEmail) {
      licenseModel.buyerId = user.getId();
      licenseModel.seats = 1;
      licenseModel.years = 1;
      licenseModel.licenseStep = Constants.LICENSE_STEP;
      model.fetchAccount(() =>
        licenseModel.fetchPlan(() => {
          licenseModel.calculatePriceData();
          controller.setRenewalDate();
          controller.renderView();
        })
      );
    } else {
      licenseModel.calculatePriceData();
      controller.setRenewalDate();
      controller.renderView();
    }

    if (applyLicense) {
      controller.enterLicenseKeyClicked();
    }
  }

  renderView() {
    if (
      Backbone.history.fragment === 'profile/billing' ||
      Backbone.history.fragment === 'profile/purchase' ||
      Backbone.history.fragment === 'profile/apply'
    ) {
      ReactDOM.render(
        <ErrorView>
          <AccountView
            controller={controller}
            model={model}
            selectedTab={Constants.PROFILE_BILLING}
            router={router}
            isPro={proCheck.hasPaidPlan()}
            expiration={model.expiration.toLocaleDateString(
              user.getLanguage(),
              {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC',
              }
            )}
            hasRecentlyExpired={model.hasRecentlyExpired}
            registration={false}
          />
        </ErrorView>,
        document.getElementById('main-content')
      );
    }
  }

  inRenewalWindow() {
    if (!proCheck.hasPaidPlan()) {
      return false;
    }

    let today = new Date();
    return model.renewalWindow.getTime() <= today.getTime();
  }

  /**
   * Change the auto renew setting for an existing license.
   */
  accountAutoRenewChanged() {
    let oldAutoRenewValue = model.autoRenew;
    model.autoRenew = !model.autoRenew;

    licenseModel.saveAutoRenew(model.autoRenew, (success) => {
      if (!success) {
        model.autoRenew = oldAutoRenewValue;
        controller.renderView();

        popup.render({
          title: translate('Unknown Error'),
          message: translate(
            'There was a problem changing your auto renew setting. Please check your connection and try again.'
          ),
        });
      } else {
        renewalNotice.closeClicked();
        header.render();
      }
    });

    controller.renderView();
  }

  renewLicenseClicked() {
    model.banner.message = '';
    model.buyingLicense = true;
    controller.renderView();
  }

  learnMoreClicked() {
    goPro.render({
      router: router,
      hideGoProButton: true,
      paywallScreen: PaywallScreens.AppAccount,
    });
  }

  upgradeClicked() {
    model.banner.message = '';
    model.buyingLicense = true;
    controller.renderView();
  }

  passwordChanged(password) {
    popup.test();
    model.password = password;
  }

  redirectToLogin(success) {
    if (success) {
      router.routeToTeacherLogin();
    } else {
      model.deletingAccount = false;
      controller.showErrorBanner(
        translate('Something went wrong. Please contact support.')
      );
    }
  }

  enterLicenseKeyClicked() {
    activateLicenseModal.render({
      activateCallback: (licenseExpiration, licenseKey) => {
        if (licenseExpiration && licenseKey) {
          legacySetPro();
          header.render();
          toast.positive(translate('License Activated!'));
          model.expiration = new Date(licenseExpiration);
          model.licenseKey = licenseKey;
          model.buyingLicense = false;
          renewalNotice.closeAfterRenewal();
          controller.renderView();
        }
      },
    });
  }

  inputChanged(id, value) {
    if (id === 'licenseSeats') {
      licenseModel.seats = parseInt(value);
      if (isNaN(licenseModel.seats)) {
        licenseModel.seats = ''; // Allow an empty input so the user needn't select the current value first.
      }
      if (licenseModel.isValidSeats()) {
        controller.seatsBlurred();
      }
    } else if (id === 'couponName') {
      licenseModel.couponName = value;
      licenseModel.setError('couponName', false);
    } else if (id === 'nameOnCard') {
      licenseModel.nameOnCard = value;
      licenseModel.setError('nameOnCard', !licenseModel.isValidNameOnCard());
    } else if (id === 'cardNumber') {
      licenseModel.cardType = window.Stripe.card.cardType(value);
      licenseModel.cardNumber = value;
      licenseModel.setError('cardNumber', !licenseModel.isValidCardNumber());
    } else if (id === 'cvc') {
      licenseModel.cvc = value;
      licenseModel.setError('cvc', !licenseModel.isValidCvc());
    }
    controller.renderView();
  }

  seatsBlurred() {
    if (!licenseModel.seats || licenseModel.seats < 1) {
      licenseModel.seats = 1;
    } else if (licenseModel.seats > Constants.MAX_DISPLAY_SEATS) {
      licenseModel.seats = Constants.MAX_DISPLAY_SEATS;
    }

    licenseModel.calculatePriceData();
    controller.renderView();
  }

  yearsChanged(years) {
    licenseModel.setYearsAndUpdateCoupon(years, controller.couponFetched);
    controller.setRenewalDate();
    licenseModel.calculatePriceData();
    controller.renderView();
  }

  seatsChanged(seats) {
    licenseModel.setSeatsAndUpdateCoupon(seats, controller.couponFetched);
    controller.setRenewalDate();
    licenseModel.calculatePriceData();
    controller.renderView();
  }

  setRenewalDate() {
    let renewalDate = new Date();
    licenseModel.setRenewalDate(renewalDate);
  }

  applyCouponClicked() {
    if (!licenseModel.isValidCouponName()) {
      dots.stop();
      licenseModel.setError('couponName', true);
    } else {
      dots.start('applyCouponButton');
      licenseModel.fetchCoupon(controller.couponFetched);
    }

    controller.renderView();
  }

  couponFetched() {
    dots.stop();

    licenseModel.calculatePriceData();

    controller.renderView();
  }

  clearCouponClicked() {
    licenseModel.clearCoupon();
    licenseModel.calculatePriceData();
    controller.renderView();
  }

  applyNowChanged() {
    licenseModel.applyNow = !licenseModel.applyNow;
    controller.renderView();
  }

  reviewAndPayClicked() {
    if (licenseModel.isValidCouponName() && !model.couponSuccess) {
      // The user forgot to apply the coupon.
      dots.start('reviewAndPayButton');

      licenseModel.fetchCoupon((success) => {
        dots.stop();

        if (success) {
          licenseModel.calculatePriceData();
          licenseModel.licenseStep = Constants.PAYMENT_STEP;
        }

        controller.setRenewalDate();
        controller.renderView();
      });
    } else {
      licenseModel.licenseStep = Constants.PAYMENT_STEP;
      controller.renderView();
    }
  }

  monthChanged(value) {
    licenseModel.month = value;
    licenseModel.setError('month', false);
    controller.renderView();
  }

  yearChanged(value) {
    licenseModel.year = value;
    licenseModel.setError('year', false);
    controller.renderView();
  }

  previousClicked() {
    licenseModel.licenseStep = Constants.LICENSE_STEP;
    controller.renderView();
  }

  purchaseClicked() {
    if (licenseModel.canPurchase()) {
      dots.start('licensePurchaseButton');
      licenseModel.makePurchase(controller.purchaseResult);
    } else {
      dots.stop();
    }

    controller.renderView();
  }

  purchaseResult(success, error) {
    const hasPaidPlan = proCheck.hasPaidPlan();
    if (!success) {
      dots.stop();

      if (error === Constants.PRICE_MISMATCH) {
        popup.render({
          title: translate('Price Change'),
          message: translate(
            'Our prices recently changed. The total for your order has been updated.'
          ),
        });
      } else if (error === Constants.INTERNAL_SERVER_ERROR) {
        controller.showErrorBanner(
          translate(
            'There was a problem completing your purchase or activating your license. Please contact Socrative Support.'
          )
        );
      } else {
        controller.showErrorBanner(error);
      }
    } else {
      let referrer = 'none';
      let secondaryReferrer = 'none';
      let paywallReferrer = null;

      const usedGetProLink = user.usedGetProLink();
      if (usedGetProLink) {
        referrer = 'get-pro-link';
        secondaryReferrer = usedGetProLink.referrer ?? 'none';
      }

      new LicensePurchaseEventAdapter({
        legacyLicenseModel: licenseModel,
        referrer: referrer,
        secondaryReferrer: secondaryReferrer,
        paywallFeature: paywallReferrer,
      }).sendAnalytics();

      if (!hasPaidPlan && licenseModel.applyNow) {
        legacySetPro();
      }

      if (licenseModel.applyNow) {
        model.setExpiration(new Date(licenseModel.renewalDate.getTime()));
      }

      toast.positive(translate('Purchase Complete!'));
      renewalNotice.closeAfterRenewal();
      licenseModel.licenseStep = Constants.COMPLETE_STEP;
    }
    controller.renderView();
  }

  showErrorBanner(message) {
    dots.stop();

    if (!message) {
      message = translate('Check your credit card information and try again.');
    }

    toast.critical(message);

    controller.renderView();

    if (window.innerWidth >= 768) {
      document.body.scrollTop = 0;
    }
  }

  closeBannerClicked() {
    model.banner.message = '';
    controller.renderView();
  }

  emailReceiptClicked() {
    model.getReceipt(controller.receiptSent);
  }

  receiptSent(success) {
    if (success) {
      toast.positive(
        translate('The receipt has been sent to {0}.').format(model.email)
      );
      controller.renderView();
    } else {
      toast.critical(
        translate(
          'An error occurred. Please check your connection and try again.'
        )
      );

      controller.renderView();
    }

    header.render();
    controller.renderView();
  }

  downloadReceiptClicked() {
    controller.startDownload();
  }

  startDownload() {
    const hiddenIFrameID = 'receiptDownloader';
    let iframe = document.getElementById(hiddenIFrameID);

    if (iframe === null) {
      iframe = document.createElement('iframe');
      iframe.id = hiddenIFrameID;
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    iframe.src =
      window.APP_CONFIG.BACKEND_HOST +
      '/lecturers/api/payment/receipt/?action=download';
  }
}

let controller = new AccountController();
module.exports = controller;
